// === IMPORTS ===
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TimeMachineFrame, TimeMachineIncident, AIAgent } from '../types';
import {
  History, Play, Pause, SkipBack, SkipForward, AlertTriangle, Server,
  MessageSquare, Zap, X, Copy, Check, RotateCcw, ShieldAlert, Terminal,
  Clock, Layers, Search, User, Radio, ChevronDown, ChevronUp, Code2,
  CornerDownRight, ArrowDownUp, Activity,
} from 'lucide-react';
import '../styles/twin-drawers-shared.css';
import '../styles/twin-icons-shared.css';
import './TimeMachineDrawer.css';

interface TimeMachineDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  frames: TimeMachineFrame[];
  incidents: TimeMachineIncident[];
  currentFrameIndex: number;
  isLive: boolean;
  onSelectFrame: (index: number) => void;
  onGoLive: () => void;
  onSelectAgent?: (agent: AIAgent) => void;
  onRestoreLayoutFromFrame?: (frame: TimeMachineFrame) => void;
  activeAgents?: AIAgent[];
}

// === COMPONENTE: DVR Y LÍNEA DE TIEMPO DEL SISTEMA ===
export const TimeMachineDrawer: React.FC<TimeMachineDrawerProps> = ({
  isOpen, onClose, frames, incidents, currentFrameIndex, isLive,
  onSelectFrame, onGoLive, onSelectAgent, onRestoreLayoutFromFrame, activeAgents = [],
}) => {
  const drawerRef = useRef<HTMLElement>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const activeCardRef = useRef<HTMLDivElement>(null);

  const [isPlayingReplay, setIsPlayingReplay] = useState<boolean>(false);
  const [replaySpeed, setReplaySpeed] = useState<number>(1);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'incidents' | 'mcp' | 'chat' | 'tasks'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const [expandedFrameId, setExpandedFrameId] = useState<string | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState<'incident' | 'events' | 'dialogues' | 'metrics'>('incident');
  const [expandedPayloadEventId, setExpandedPayloadEventId] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState<boolean>(false);
  const [copiedJson, setCopiedJson] = useState<boolean>(false);

  const safeIndex = Math.min(Math.max(0, currentFrameIndex), Math.max(0, frames.length - 1));
  const currentFrame = frames[safeIndex];

  useEffect(() => {
    if (!isPlayingReplay) return;
    const interval = setInterval(() => {
      if (safeIndex >= frames.length - 1) setIsPlayingReplay(false);
      else onSelectFrame(safeIndex + 1);
    }, 1000 / replaySpeed);
    return () => clearInterval(interval);
  }, [isPlayingReplay, safeIndex, frames.length, replaySpeed, onSelectFrame]);

  useEffect(() => { if (isLive) setIsPlayingReplay(false); }, [isLive]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (currentFrame && !expandedFrameId) setExpandedFrameId(currentFrame.id);
  }, [currentFrame?.id]);

  useEffect(() => {
    if (isOpen && activeCardRef.current && timelineContainerRef.current) {
      activeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentFrameIndex, isOpen]);

  const displayedFrames = useMemo(() => {
    let result = frames.map((frame, originalIndex) => ({
      frame, originalIndex,
      incident: incidents.find((inc) => inc.clockTime === frame.clockTime || inc.frameIndex === originalIndex),
    }));

    if (activeFilter === 'incidents') {
      result = result.filter((item) => item.frame.isIncident || item.incident);
    } else if (activeFilter === 'mcp') {
      result = result.filter((item) => item.frame.events.some((ev) => ev.type === 'mcp_call' || ev.mcpServer));
    } else if (activeFilter === 'chat') {
      result = result.filter((item) => (item.frame.activeAgentDialogues && item.frame.activeAgentDialogues.length > 0) || item.frame.events.some((ev) => ev.type === 'agent_chat'));
    } else if (activeFilter === 'tasks') {
      result = result.filter((item) => item.frame.events.some((ev) => ev.type === 'task_assign' || ev.type === 'task_completed'));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const timeMatch = item.frame.clockTime.toLowerCase().includes(q);
        const incidentMatch = item.incident?.title.toLowerCase().includes(q) || item.incident?.summary.toLowerCase().includes(q);
        const eventMatch = item.frame.events.some((ev) =>
          ev.title.toLowerCase().includes(q) || ev.description.toLowerCase().includes(q) ||
          (ev.agentName && ev.agentName.toLowerCase().includes(q)) || (ev.mcpServer && ev.mcpServer.toLowerCase().includes(q))
        );
        const dialogueMatch = item.frame.activeAgentDialogues?.some((d) => d.message.toLowerCase().includes(q));
        return timeMatch || incidentMatch || eventMatch || dialogueMatch;
      });
    }

    if (sortOrder === 'newest') result.reverse();
    return result;
  }, [frames, incidents, activeFilter, searchQuery, sortOrder]);

  const handleStepBack = () => { if (safeIndex > 0) onSelectFrame(safeIndex - 1); };
  const handleStepForward = () => { if (safeIndex < frames.length - 1) onSelectFrame(safeIndex + 1); else onGoLive(); };
  const handleTogglePlayReplay = () => { if (isLive) setIsPlayingReplay(true); else setIsPlayingReplay((prev) => !prev); };

  const handleSelectIncidentDirectly = (incident: TimeMachineIncident) => {
    onSelectFrame(incident.frameIndex);
    setExpandedFrameId(frames[incident.frameIndex]?.id || null);
    setActiveDetailTab('incident');
  };

  const handleCopyAuditReport = (targetIncident: TimeMachineIncident) => {
    const textReport = `=====================================================
FORENSIC AUDIT REPORT - TIME MACHINE DVR
=====================================================
INCIDENT: ${targetIncident.title}
TIMESTAMP: ${targetIncident.clockTime}
AGENT INVOLVED: ${targetIncident.agentName} (${targetIncident.role})
ERROR CATEGORY: ${targetIncident.category}

EXECUTIVE SUMMARY:
${targetIncident.summary}

CAUSAL CHAIN & STEP SEQUENCE:
${targetIncident.causalChain.map((c) => `[Step ${c.stepNumber}] ${c.timestamp} - ${c.stepTitle}\n  Agent: ${c.agent}\n  Detail: ${c.detail}\n  Payload: ${JSON.stringify(c.rawPayload || {})}`).join('\n\n')}

DIAGNOSED ROOT CAUSE:
${targetIncident.rootCause}

RECOMMENDED REMEDIATION ACTIONS:
${targetIncident.recommendedFix}
=====================================================`;
    navigator.clipboard?.writeText(textReport);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2200);
  };

  const handleCopyFrameJson = (targetFrame: TimeMachineFrame) => {
    navigator.clipboard?.writeText(JSON.stringify(targetFrame, null, 2));
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2200);
  };

  const handleFocusAgentInCanvas = (agentNameOrId: string) => {
    if (!onSelectAgent) return;
    const matched = activeAgents.find((a) => a.id === agentNameOrId || a.name.toLowerCase().includes(agentNameOrId.toLowerCase())) || activeAgents[0];
    if (matched) onSelectAgent(matched);
  };

  const activeIncidentForCurrentFrame = incidents.find((inc) => inc.clockTime === currentFrame?.clockTime || inc.frameIndex === safeIndex);

  // === RENDER ===
  return (
    <aside
      ref={drawerRef}
      id="time-machine-dvr-drawer"
      aria-label="Side Drawer: Time Machine DVR & Timeline Audit"
      className={`twin-drawer twin-tmdrawer ${isOpen ? 'twin-drawer--open' : 'twin-drawer--closed'}`}
    >
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className={`twin-tmdrawer-status-icon ${isLive ? 'twin-tmdrawer-status-icon--live' : 'twin-tmdrawer-status-icon--replay'}`}>
            <History className="twin-icon-sm" />
          </div>
          <div className="twin-tmdrawer-header-text">
            <div className="twin-tmdrawer-title-row">
              <h2 className="twin-drawer-title">DVR & Time Machine</h2>
              {isLive ? (
                <span className="twin-tmdrawer-live-badge"><span className="twin-tmdrawer-live-dot" />LIVE</span>
              ) : (
                <span className="twin-tmdrawer-replay-badge"><RotateCcw className="twin-icon-xs" />REPLAY {currentFrame?.clockTime}</span>
              )}
            </div>
            <p className="twin-drawer-subtitle">Forensic timeline, MCP traces, and event diagnostics</p>
          </div>
        </div>

        <div className="twin-tmdrawer-header-actions">
          {!isLive && (
            <button type="button" onClick={onGoLive} className="twin-tmdrawer-golive-btn" title="Return immediately to real-time simulation">
              <Radio className="twin-icon-sm" /><span>Back to LIVE</span>
            </button>
          )}
          <button type="button" onClick={onClose} className="twin-drawer-close-btn" title="Close drawer (Esc)"><X className="twin-icon-sm" /></button>
        </div>
      </div>

      {/* === PANEL DE CONTROL DVR === */}
      <div className="twin-tmdrawer-control-deck">
        <div className="twin-tmdrawer-transport-row">
          <div className="twin-tmdrawer-transport-btns">
            <button type="button" onClick={handleStepBack} disabled={safeIndex === 0} className="twin-tmdrawer-transport-btn" title="Step back 1 frame"><SkipBack className="twin-icon-sm" /></button>
            <button type="button" onClick={handleTogglePlayReplay} className={`twin-tmdrawer-play-btn ${isPlayingReplay ? 'twin-tmdrawer-play-btn--pause' : ''}`} title={isPlayingReplay ? 'Pause replay' : 'Play recording'}>
              {isPlayingReplay ? (<><Pause className="twin-icon-sm" /><span>Pause</span></>) : (<><Play className="twin-icon-sm" /><span>Play</span></>)}
            </button>
            <button type="button" onClick={handleStepForward} disabled={isLive && safeIndex >= frames.length - 1} className="twin-tmdrawer-transport-btn" title="Step forward 1 frame"><SkipForward className="twin-icon-sm" /></button>
          </div>

          <div className="twin-tmdrawer-speed-pills">
            {[0.5, 1, 2, 5].map((spd) => (
              <button key={spd} type="button" onClick={() => setReplaySpeed(spd)} className={`twin-tmdrawer-speed-pill ${replaySpeed === spd ? 'twin-tmdrawer-speed-pill--active' : ''}`}>{spd}x</button>
            ))}
          </div>

          <div className="twin-tmdrawer-clock-badge">
            <Clock className="twin-icon-xs" />
            <span className="twin-tmdrawer-clock-time">{currentFrame?.clockTime || '00:00:00'}</span>
            <span className="twin-tmdrawer-clock-count">({safeIndex + 1}/{frames.length})</span>
          </div>
        </div>

        <div className="twin-tmdrawer-scrub-row">
          <span className="twin-tmdrawer-scrub-label">0s</span>
          <input
            type="range" min={0} max={Math.max(0, frames.length - 1)} value={safeIndex}
            onChange={(e) => { const val = parseInt(e.target.value, 10); if (!isNaN(val)) { onSelectFrame(val); if (val === frames.length - 1) onGoLive(); } }}
            className="twin-range twin-range--tm"
          />
          <span className="twin-tmdrawer-scrub-label">{frames[frames.length - 1]?.clockTime || 'LIVE'}</span>
        </div>

        {incidents.length > 0 && (
          <div className="twin-tmdrawer-incidents-bar">
            <span className="twin-tmdrawer-incidents-label"><AlertTriangle className="twin-icon-xs" />Incidents:</span>
            {incidents.map((inc) => (
              <button key={inc.id} type="button" onClick={() => handleSelectIncidentDirectly(inc)} className={`twin-tmdrawer-incident-chip ${safeIndex === inc.frameIndex ? 'twin-tmdrawer-incident-chip--active' : ''}`} title={`Jump to incident at ${inc.clockTime}: ${inc.title}`}>
                <span className="twin-tmdrawer-incident-chip-time">{inc.clockTime}</span>
                <span className="twin-tmdrawer-incident-chip-title">{inc.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* === BARRA DE BÚSQUEDA Y FILTROS === */}
      <div className="twin-tmdrawer-filterbar">
        <div className="twin-tmdrawer-filter-pills">
          {[
            { id: 'all', label: 'All', icon: Layers }, { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
            { id: 'mcp', label: 'MCP Tools', icon: Zap }, { id: 'chat', label: 'Dialogues', icon: MessageSquare },
            { id: 'tasks', label: 'Tasks', icon: Check },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} type="button" onClick={() => setActiveFilter(tab.id as any)} className={`twin-tmdrawer-filter-pill ${activeFilter === tab.id ? 'twin-tmdrawer-filter-pill--active' : ''}`}>
                <Icon className="twin-icon-xs" /><span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="twin-tmdrawer-search-sort">
          <div className="twin-tmdrawer-search-box">
            <Search className="twin-icon-xs twin-tmdrawer-search-icon" />
            <input type="text" placeholder="Search event..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="twin-tmdrawer-search-input" />
          </div>
          <button type="button" onClick={() => setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'))} className="twin-tmdrawer-sort-btn" title={sortOrder === 'newest' ? 'Newest on top' : 'Oldest on top'}>
            <ArrowDownUp className="twin-icon-sm" />
          </button>
        </div>
      </div>

      {/* === LÍNEA DE TIEMPO VERTICAL === */}
      <div ref={timelineContainerRef} className="twin-tmdrawer-timeline">
        <div className="twin-tmdrawer-timeline-track" />

        {displayedFrames.length === 0 ? (
          <div className="twin-tmdrawer-empty-state">
            <History className="twin-icon-lg" />
            <p className="twin-tmdrawer-empty-title">No frames found with the current filter</p>
            <button type="button" onClick={() => { setActiveFilter('all'); setSearchQuery(''); }} className="twin-tmdrawer-empty-reset">Reset filters</button>
          </div>
        ) : (
          displayedFrames.map(({ frame, originalIndex, incident }) => {
            const isCurrentActive = originalIndex === safeIndex;
            const isLiveFrame = originalIndex === frames.length - 1;
            const isIncident = Boolean(frame.isIncident || incident);
            const isExpanded = expandedFrameId === frame.id;

            return (
              <div key={frame.id} ref={isCurrentActive ? activeCardRef : null} className={`twin-tmdrawer-frame-wrap ${isCurrentActive ? 'twin-tmdrawer-frame-wrap--active' : ''}`}>
                <button
                  type="button"
                  onClick={() => { onSelectFrame(originalIndex); if (isLiveFrame) onGoLive(); setExpandedFrameId(frame.id); }}
                  className={`twin-tmdrawer-node ${isCurrentActive ? (isIncident ? 'twin-tmdrawer-node--active-rose' : isLiveFrame ? 'twin-tmdrawer-node--active-emerald' : 'twin-tmdrawer-node--active-cyan') : (isIncident ? 'twin-tmdrawer-node--rose' : isLiveFrame ? 'twin-tmdrawer-node--emerald' : 'twin-tmdrawer-node--default')}`}
                  title={`Travel to ${frame.clockTime}`}
                >
                  {isIncident ? <AlertTriangle className="twin-icon-xs" /> : isLiveFrame ? <Radio className="twin-icon-xs" /> : <Clock className="twin-icon-xs" />}
                </button>

                <div className={`twin-tmdrawer-card ${isCurrentActive ? (isIncident ? 'twin-tmdrawer-card--active-rose' : isLiveFrame ? 'twin-tmdrawer-card--active-emerald' : 'twin-tmdrawer-card--active-cyan') : ''}`}>
                  <div onClick={() => { onSelectFrame(originalIndex); if (isLiveFrame) onGoLive(); setExpandedFrameId(isExpanded ? null : frame.id); }} className="twin-tmdrawer-card-header">
                    <div className="twin-tmdrawer-card-header-left">
                      <span className="twin-tmdrawer-card-time">{frame.clockTime}</span>
                      {isLiveFrame && <span className="twin-tmdrawer-tag twin-tmdrawer-tag--emerald">LIVE</span>}
                      {isIncident && <span className="twin-tmdrawer-tag twin-tmdrawer-tag--rose">{incident?.category || 'Alarm'}</span>}
                      <span className="twin-tmdrawer-card-events-count">
                        {frame.events.length} event{frame.events.length !== 1 ? 's' : ''}
                        {frame.activeAgentDialogues && frame.activeAgentDialogues.length > 0 ? ` • ${frame.activeAgentDialogues.length} dialogue` : ''}
                      </span>
                    </div>
                    <div className="twin-tmdrawer-card-header-right">
                      {isCurrentActive && <span className="twin-tmdrawer-active-tag">ACTIVE IN CANVAS</span>}
                      <button type="button" onClick={(e) => { e.stopPropagation(); setExpandedFrameId(isExpanded ? null : frame.id); }} className="twin-tmdrawer-chevron-btn" title={isExpanded ? 'Collapse details' : 'Expand details'}>
                        {isExpanded ? <ChevronUp className="twin-icon-sm" /> : <ChevronDown className="twin-icon-sm" />}
                      </button>
                    </div>
                  </div>

                  <div className="twin-tmdrawer-card-summary">
                    {incident ? (
                      <div className="twin-tmdrawer-summary-incident"><AlertTriangle className="twin-icon-sm" /><span>{incident.title}</span></div>
                    ) : frame.events[0] ? (
                      <p className="twin-tmdrawer-summary-text"><span className="twin-tmdrawer-summary-title">{frame.events[0].title}:</span> {frame.events[0].description}</p>
                    ) : (
                      <p className="twin-tmdrawer-summary-idle">Stable simulation frame</p>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="twin-tmdrawer-expanded">
                      <div className="twin-tmdrawer-expanded-actions">
                        <div className="twin-tmdrawer-expanded-actions-left">
                          {incident && (
                            <button type="button" onClick={() => handleFocusAgentInCanvas(incident.agentName)} className="twin-tmdrawer-action-chip" title={`Focus agent ${incident.agentName} on canvas`}>
                              <User className="twin-icon-xs" /><span>Focus {incident.agentName}</span>
                            </button>
                          )}
                          <button type="button" onClick={() => handleCopyFrameJson(frame)} className="twin-tmdrawer-action-chip" title="Copy frame JSON">
                            {copiedJson ? (<><Check className="twin-icon-xs twin-stat-positive" /><span className="twin-stat-positive">JSON Copied</span></>) : (<><Code2 className="twin-icon-xs" /><span>Copy JSON</span></>)}
                          </button>
                          {incident && (
                            <button type="button" onClick={() => handleCopyAuditReport(incident)} className="twin-tmdrawer-action-chip twin-tmdrawer-action-chip--rose" title="Copy full forensic report">
                              {copiedReport ? (<><Check className="twin-icon-xs twin-stat-positive" /><span className="twin-stat-positive">Report Copied</span></>) : (<><Copy className="twin-icon-xs" /><span>Forensic Report</span></>)}
                            </button>
                          )}
                        </div>
                        {onRestoreLayoutFromFrame && (
                          <button type="button" onClick={() => onRestoreLayoutFromFrame(frame)} className="twin-tmdrawer-action-chip twin-tmdrawer-action-chip--amber" title="Restore office layout and positions from this frame">
                            <RotateCcw className="twin-icon-xs" /><span>Restore Office</span>
                          </button>
                        )}
                      </div>

                      <div className="twin-tmdrawer-subtabs">
                        {incident && (
                          <button type="button" onClick={() => setActiveDetailTab('incident')} className={`twin-tmdrawer-subtab twin-tmdrawer-subtab--rose ${activeDetailTab === 'incident' ? 'twin-tmdrawer-subtab--active-rose' : ''}`}>
                            <ShieldAlert className="twin-icon-xs" /><span>Causal Diagnostics</span>
                          </button>
                        )}
                        <button type="button" onClick={() => setActiveDetailTab('events')} className={`twin-tmdrawer-subtab twin-tmdrawer-subtab--cyan ${activeDetailTab === 'events' || (!incident && activeDetailTab === 'incident') ? 'twin-tmdrawer-subtab--active-cyan' : ''}`}>
                          <Terminal className="twin-icon-xs" /><span>Events ({frame.events.length})</span>
                        </button>
                        {frame.activeAgentDialogues && frame.activeAgentDialogues.length > 0 && (
                          <button type="button" onClick={() => setActiveDetailTab('dialogues')} className={`twin-tmdrawer-subtab twin-tmdrawer-subtab--purple ${activeDetailTab === 'dialogues' ? 'twin-tmdrawer-subtab--active-purple' : ''}`}>
                            <MessageSquare className="twin-icon-xs" /><span>Dialogues ({frame.activeAgentDialogues.length})</span>
                          </button>
                        )}
                        <button type="button" onClick={() => setActiveDetailTab('metrics')} className={`twin-tmdrawer-subtab twin-tmdrawer-subtab--emerald ${activeDetailTab === 'metrics' ? 'twin-tmdrawer-subtab--active-emerald' : ''}`}>
                          <Activity className="twin-icon-xs" /><span>Metrics</span>
                        </button>
                      </div>

                      {/* TAB: INCIDENT */}
                      {incident && activeDetailTab === 'incident' && (
                        <div className="twin-tmdrawer-tabcontent">
                          <div className="twin-tmdrawer-incident-summary">
                            <div className="twin-tmdrawer-incident-summary-header">
                              <span className="twin-tmdrawer-incident-agent">{incident.agentName} ({incident.role})</span>
                              <span className="twin-tmdrawer-incident-code">Code: {incident.category}</span>
                            </div>
                            <p className="twin-tmdrawer-incident-text">{incident.summary}</p>
                          </div>

                          <div className="twin-tmdrawer-causal-block">
                            <h4 className="twin-tmdrawer-causal-title"><CornerDownRight className="twin-icon-xs" />Execution Causal Chain</h4>
                            <div className="twin-tmdrawer-causal-list">
                              {incident.causalChain.map((step) => {
                                const isStepError = step.stepType === 'error';
                                return (
                                  <div key={step.stepNumber} className={`twin-tmdrawer-causal-step ${isStepError ? 'twin-tmdrawer-causal-step--error' : ''}`}>
                                    <span className={`twin-tmdrawer-causal-num ${isStepError ? 'twin-tmdrawer-causal-num--error' : ''}`}>{step.stepNumber}</span>
                                    <div className="twin-tmdrawer-causal-content">
                                      <div className="twin-tmdrawer-causal-header">
                                        <span className="twin-tmdrawer-causal-step-title">{step.stepTitle}</span>
                                        <span className="twin-tmdrawer-causal-step-time">{step.timestamp}</span>
                                      </div>
                                      <p className="twin-tmdrawer-causal-detail">{step.detail}</p>
                                      {step.rawPayload && <pre className="twin-tmdrawer-json-block">{JSON.stringify(step.rawPayload, null, 2)}</pre>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="twin-tmdrawer-rootcause-grid">
                            <div className="twin-tmdrawer-rootcause-box">
                              <span className="twin-tmdrawer-rootcause-label twin-color-amber">Detected Root Cause</span>
                              <p className="twin-tmdrawer-rootcause-text">{incident.rootCause}</p>
                            </div>
                            <div className="twin-tmdrawer-rootcause-box twin-tmdrawer-rootcause-box--fix">
                              <span className="twin-tmdrawer-rootcause-label twin-stat-positive">Recommended Remediation</span>
                              <p className="twin-tmdrawer-rootcause-text-fix">{incident.recommendedFix}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* TAB: EVENTS */}
                      {(activeDetailTab === 'events' || (!incident && activeDetailTab === 'incident')) && (
                        <div className="twin-tmdrawer-tabcontent">
                          {frame.events.length === 0 ? (
                            <p className="twin-tmdrawer-no-events">No events recorded in this frame.</p>
                          ) : (
                            frame.events.map((ev) => {
                              const isMcp = ev.type === 'mcp_call';
                              const isPayloadOpen = expandedPayloadEventId === ev.id;
                              return (
                                <div key={ev.id} className="twin-tmdrawer-event-card">
                                  <div className="twin-tmdrawer-event-header">
                                    <div className="twin-tmdrawer-event-title-row">
                                      {isMcp ? <Zap className="twin-icon-sm twin-color-cyan" /> : ev.type === 'error' ? <AlertTriangle className="twin-icon-sm twin-color-rose" /> : <Terminal className="twin-icon-sm" />}
                                      <span className="twin-tmdrawer-event-title">{ev.title}</span>
                                    </div>
                                    <span className="twin-tmdrawer-event-time">{ev.clockTime}</span>
                                  </div>
                                  <p className="twin-tmdrawer-event-desc">{ev.description}</p>

                                  {(ev.mcpPayload || ev.mcpResponse) && (
                                    <div className="twin-tmdrawer-payload-toggle-wrap">
                                      <button type="button" onClick={() => setExpandedPayloadEventId(isPayloadOpen ? null : ev.id)} className="twin-tmdrawer-payload-toggle">
                                        <Code2 className="twin-icon-xs" /><span>{isPayloadOpen ? 'Hide MCP payload' : 'View MCP payload & response'}</span>
                                      </button>
                                      {isPayloadOpen && (
                                        <div className="twin-tmdrawer-payload-body">
                                          {ev.mcpPayload && (<div><span className="twin-tmdrawer-payload-label">Request:</span><pre className="twin-tmdrawer-json-block">{JSON.stringify(ev.mcpPayload, null, 2)}</pre></div>)}
                                          {ev.mcpResponse && (<div><span className="twin-tmdrawer-payload-label">Response:</span><pre className="twin-tmdrawer-json-block twin-tmdrawer-json-block--response">{JSON.stringify(ev.mcpResponse, null, 2)}</pre></div>)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}

                      {/* TAB: DIALOGUES */}
                      {activeDetailTab === 'dialogues' && frame.activeAgentDialogues && (
                        <div className="twin-tmdrawer-tabcontent">
                          {frame.activeAgentDialogues.map((dlg, idx) => (
                            <div key={idx} className="twin-tmdrawer-dialogue-card">
                              <div className="twin-tmdrawer-dialogue-header"><MessageSquare className="twin-icon-xs" /><span>{dlg.fromAgentId} ➔ {dlg.toAgentId}</span></div>
                              <p className="twin-tmdrawer-dialogue-message">"{dlg.message}"</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* TAB: METRICS */}
                      {activeDetailTab === 'metrics' && (
                        <div className="twin-tmdrawer-metrics-grid">
                          <div className="twin-tmdrawer-metric-box"><span className="twin-tmdrawer-metric-label">Completed Tasks</span><span className="twin-tmdrawer-metric-value">{frame.statsSnapshot?.totalTasksCompleted ?? '--'}</span></div>
                          <div className="twin-tmdrawer-metric-box"><span className="twin-tmdrawer-metric-label">Active Queue</span><span className="twin-tmdrawer-metric-value">{frame.statsSnapshot?.tasksInQueue ?? '--'}</span></div>
                          <div className="twin-tmdrawer-metric-box"><span className="twin-tmdrawer-metric-label">Throughput</span><span className="twin-tmdrawer-metric-value twin-color-cyan">{frame.statsSnapshot?.throughputTasksPerMin ?? '--'} t/min</span></div>
                          <div className="twin-tmdrawer-metric-box"><span className="twin-tmdrawer-metric-label">Efficiency</span><span className="twin-tmdrawer-metric-value twin-stat-positive">{frame.statsSnapshot?.officeEfficiencyPct ?? '--'}%</span></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* === FOOTER === */}
      <div className="twin-drawer-footer twin-drawer-footer--info">
        {/* <div className="twin-tmdrawer-footer-summary"><History className="twin-icon-sm twin-color-theme-accent" /><span>{frames.length} recorded moments ({incidents.length} alarms)</span></div> */}
        <span className="twin-drawer-footer-info">
          <History className="twin-icon-sm twin-color-theme-accent" />
          {frames.length} recorded moments ({incidents.length} alarms)
        </span>

        {/* <button type="button" onClick={onClose} className="twin-btn twin-btn--secondary">Close</button> */}
        <button type="button" onClick={onClose} className="twin-btn-cancel">
          Close
        </button>
      </div>
    </aside>
  );
};